import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Navbar from "../../components/common/Layout/Navbar";
import "./Auth.css";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const navigate = useNavigate();
  const { login, resendVerificationEmail } = useAuth();
  const errorRef = useRef(null);

  const validate = () => {
    const errors = {};
    if (!email.trim()) {
      errors.email = "Email is required.";
    } else if (!EMAIL_REGEX.test(email.trim())) {
      errors.email = "Enter a valid email address.";
    }
    if (!password) {
      errors.password = "Password is required.";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setVerificationRequired(false);
    setResendMessage("");

    if (!validate()) return;

    setLoading(true);

    try {
      // Prefer the value returned directly from login() so we don't race
      // against localStorage being written. Falls back to localStorage
      // only if login() doesn't return the user object.
      const result = await login(email.trim(), password);
      const user =
        result?.user ||
        result?.data?.user ||
        result ||
        JSON.parse(localStorage.getItem("user") || "null");

      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
      }

      if (user?.Role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Login failed. Please try again.";
      setError(errorMessage);
      setVerificationRequired(
        errorMessage.toLowerCase().includes("verify your email address"),
      );
      // Move focus to the error so screen reader users hear it immediately
      requestAnimationFrame(() => errorRef.current?.focus());
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    setResendMessage("");
    try {
      const response = await resendVerificationEmail(email.trim());
      setResendMessage(
        response.message ||
          "If your account needs verification, a new verification email has been sent.",
      );
    } catch (resendError) {
      setResendMessage(
        resendError.message || "Could not resend the verification email.",
      );
    } finally {
      setResendingVerification(false);
    }
  };

  const handleFieldChange = (setter, field) => (e) => {
    setter(e.target.value);
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    if (error) {
      setError("");
      setVerificationRequired(false);
      setResendMessage("");
    }
  };

  return (
    <>
      <Navbar variant="auth" />
      <main className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Welcome back</h1>
            <p>Sign in to your Toora Flex account</p>
          </div>

          {error && (
            <div
              className="alert alert-error"
              role="alert"
              aria-live="assertive"
              tabIndex={-1}
              ref={errorRef}
            >
              {error}
              {verificationRequired && (
                <>
                  <p>
                    Check your inbox and spam or junk folder for the
                    verification link.
                  </p>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleResendVerification}
                    disabled={resendingVerification || !email.trim()}
                  >
                    {resendingVerification
                      ? "Sending..."
                      : "Resend verification email"}
                  </button>
                  {resendMessage && (
                    <p role="status" aria-live="polite">
                      {resendMessage}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={handleFieldChange(setEmail, "email")}
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
                disabled={loading}
                aria-invalid={Boolean(fieldErrors.email)}
                aria-describedby={fieldErrors.email ? "email-error" : undefined}
                className={fieldErrors.email ? "error" : ""}
              />
              {fieldErrors.email && (
                <span className="error-text" id="email-error">
                  {fieldErrors.email}
                </span>
              )}
            </div>

            <div className="form-group password-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={password}
                  onChange={handleFieldChange(setPassword, "password")}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={loading}
                  aria-invalid={Boolean(fieldErrors.password)}
                  aria-describedby={
                    fieldErrors.password ? "password-error" : undefined
                  }
                  className={fieldErrors.password ? "error" : ""}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i
                    className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}
                  ></i>
                </button>
              </div>
              {fieldErrors.password && (
                <span className="error-text" id="password-error">
                  {fieldErrors.password}
                </span>
              )}
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
                <span>Remember me</span>
              </label>
              <Link to="/forgot-password" className="forgot-link">
                Forgot password?
              </Link>
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <span className="btn-loading">
                  <span className="spinner" aria-hidden="true" />
                  Signing in…
                </span>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              Don&apos;t have an account? <Link to="/register">Sign up</Link>
            </p>
          </div>
        </div>
      </main>
    </>
  );
};

export default Login;
